import { useEffect, useReducer, useRef } from 'react';
import { ArrowUp } from 'lucide-react';
import { IconButton, Typography } from '../ui';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { isImeComposing } from '../../../lib/ime-composition';
import { laneLabel, renderLaneLine, type WireOwnerEvent } from '../../../lib/outreach-lane';
import { parseStage, stageBiValue } from '../../../lib/outreach-stage';
import {
  canSendComposer,
  composerReducer,
  composerRequestBody,
  initialComposerState,
  mintClientNoteId,
  nextRefetchDelay,
  parseTellAgentResponse,
} from '../../../lib/tell-agent-composer';
import { rereadAfterOwnerAction } from '../../../lib/owner-note-rereads';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import contactStyles from '../contacts/contacts.module.css';
import styles from './outreach.module.css';

export type TellAgentScope =
  /** A thread: the note is about THIS outreach. `stage` is the thread's raw `OUTREACH_STAGE_*`, for BI. */
  | { kind: 'thread'; outreachId: string; contactName: string; stage?: string | null }
  /** A contact with no thread to put the note in yet — replaces the old "Reach out" seed chat. */
  | { kind: 'contact'; personId: string; name: string };

/**
 * "Tell {agent}" — the one way to say something about a conversation, on every thread and on the contact
 * screen (outreach visibility phase 4, `native-apps/shared/outreach-composer/contract.json`).
 *
 * What the owner types goes to their OWN agent, never straight to the contact: the route records an
 * owner note and hands it to the agent as a USER turn, and the agent sends it on the thread (reshaped
 * only for the channel), or acts on it when it is an instruction. That is why the field is text only —
 * no attachments, no dictation — and why nothing navigates on send: the composer stays where the owner
 * is, a TOLD/PENDING line appears, and the reads are re-run at +3 s / +10 s / +30 s on ONE owned timer
 * until the host says the agent's answer is visible (`isSettled`, normally `ownerNoteActionVisible`: the
 * note settled AND the thread shows it) or the schedule runs out.
 *
 * WHAT is re-read is not the host's choice: every tick runs `rereadAfterOwnerAction(refetch)` — the
 * contact-book stages, the bridge's `/api/outreaches` rows, and the host's own read — and unmounting after a
 * send (the dialog closing, or stepping to another thread) runs it once more, because the agent's send or
 * close often lands after the schedule. Before this, each host re-read only its own person read, and the
 * Home rows behind the dialog kept "Waiting on … since 12:27" after the agent had answered and closed.
 *
 * The state is `lib/tell-agent-composer.ts` (a pure reducer, unit-tested). The parent owns its read:
 * `refetch` re-runs it and `isSettled` answers from the data it holds.
 */
export function TellAgentComposer({
  scope,
  page,
  refetch,
  isSettled,
  onSent,
  serverEventFor,
  inSheet = false,
}: {
  scope: TellAgentScope;
  /** The BI page the composer sits on: `contact_thread`, `contact_detail`, or the task sheet's origin. */
  page: string;
  /** The host's own extra read (the person-scoped contacts read, which carries the owner's lane), run beside
   *  the shared set in `lib/owner-note-refresh.ts`. */
  refetch?: () => unknown;
  /** Whether the agent's answer to the note is visible, judged from the data the parent currently holds
   *  (`ownerNoteActionVisible` / `ownerNoteActionVisibleOnContact`). */
  isSettled: (noteId: string) => boolean;
  /** Thread scope: the optimistic TOLD/PENDING item, for the parent's transcript. */
  onSent?: (event: WireOwnerEvent) => void;
  /** Contact scope: the server's own event for the sent note, once a re-read carries it — it replaces the
   *  optimistic TOLD/PENDING line under the composer, the same way a thread's lane replaces its item. */
  serverEventFor?: (noteId: string) => WireOwnerEvent | undefined;
  /** In the viewer's bottom sheet: lift the composer clear of the phone's home indicator. */
  inSheet?: boolean;
}) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const [state, dispatch] = useReducer(composerReducer, initialComposerState);
  const tr = t as (key: string, params?: Record<string, unknown>) => string;

  // The latest props, for the timer callback — a timer outlives the render that armed it.
  const live = useRef({ refetch, isSettled });
  live.current = { refetch, isSettled };

  // ONE owned refetch timer. A new send restarts it; leaving the screen clears it (native rule 14e).
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingNote = useRef<string | null>(null);
  // Whether this composer sent anything — its unmount then re-reads once more (see the header).
  const sentAny = useRef(false);
  const stopRefetch = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    pendingNote.current = null;
  };
  const scheduleRefetch = (noteId: string, step: number) => {
    const delay = nextRefetchDelay(step, live.current.isSettled(noteId));
    if (delay === null) {
      stopRefetch();
      return;
    }
    timer.current = setTimeout(() => {
      timer.current = null;
      if (pendingNote.current !== noteId) return;
      // A failed re-read is not the end of the schedule: `rereadAfterOwnerAction` never rejects.
      void rereadAfterOwnerAction(live.current.refetch).finally(() => {
        if (pendingNote.current === noteId) scheduleRefetch(noteId, step + 1);
      });
    }, delay);
  };
  useEffect(
    () => () => {
      stopRefetch();
      if (sentAny.current) void rereadAfterOwnerAction();
    },
    [],
  );
  // Stop early the moment the data in hand shows the note settled, rather than waiting for the next tick.
  useEffect(() => {
    if (pendingNote.current && isSettled(pendingNote.current)) stopRefetch();
  });

  // A second Enter / click before React re-renders must not post the draft twice.
  const inFlight = useRef(false);
  const submit = async () => {
    if (inFlight.current || !canSendComposer(state)) return;
    const sending = composerReducer(state, { type: 'send', mintId: mintClientNoteId });
    const target =
      scope.kind === 'thread' ? { kind: 'thread' as const, outreachId: scope.outreachId } : { kind: 'contact' as const, personId: scope.personId };
    const body = composerRequestBody(sending, target, 'WEB');
    if (!body) return;
    dispatch({ type: 'send', mintId: () => sending.clientNoteId as string });
    inFlight.current = true;
    try {
      const res = await apiFetch(`${API_BASE}/api/tell-agent-${scope.kind === 'thread' ? 'outreach' : 'contact'}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const landed = res.ok ? parseTellAgentResponse(await res.json().catch(() => null)) : null;
      if (!landed) {
        dispatch({ type: 'error' });
        return;
      }
      const at = new Date().toISOString();
      dispatch({ type: 'success', noteId: landed.noteId, conversationId: landed.conversationId, at });
      sentAny.current = true;
      if (scope.kind === 'thread') {
        onSent?.({
          kind: 'OUTREACH_OWNER_EVENT_TOLD',
          text: body.text,
          at,
          noteId: landed.noteId,
          conversationId: landed.conversationId,
          noteState: 'OUTREACH_OWNER_NOTE_STATE_PENDING',
        });
        const stage = parseStage(scope.stage);
        logPageClickBi(
          { page_name: page, button_name: 'tell_agent_send' },
          { additionalFields: { scope: 'thread', outreach_id: scope.outreachId, ...(stage ? { stage: stageBiValue(stage) } : {}) } },
        );
      } else {
        logPageClickBi({ page_name: page, button_name: 'tell_agent_send' }, { additionalFields: { scope: 'contact' } });
      }
      stopRefetch();
      pendingNote.current = landed.noteId;
      scheduleRefetch(landed.noteId, 0);
    } catch {
      dispatch({ type: 'error' });
    } finally {
      inFlight.current = false;
    }
  };

  const sending = state.phase === 'sending';
  const placeholder =
    scope.kind === 'thread'
      ? t('outreach.composer.placeholder', { maestroAgentName: agent.name })
      : t('outreach.composer.contactPlaceholder', { maestroAgentName: agent.name, name: scope.name });
  // Contact scope has no thread to put the note in yet, so the same TOLD/PENDING line sits under the
  // composer. It carries no "Open chat": that action belongs to a thread (its BI names the outreach).
  const contactLine =
    scope.kind === 'contact' && state.sent
      ? laneLabel(
          serverEventFor?.(state.sent.noteId) ?? {
            kind: 'OUTREACH_OWNER_EVENT_TOLD',
            text: state.sent.text,
            at: state.sent.at,
            noteId: state.sent.noteId,
            noteState: 'OUTREACH_OWNER_NOTE_STATE_PENDING',
          },
          new Date(),
        )
      : null;

  return (
    <form
      className={inSheet ? contactStyles.viewerComposer : styles.composer}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className={contactStyles.viewerComposerBox}>
        {/* The send failed — the draft (and its clientNoteId) is kept for a retry. Nothing reached anyone. */}
        {state.phase === 'failedKeepsDraft' ? (
          <Typography variant="p3" className={contactStyles.viewerSendError} role="alert">
            {t('outreach.composer.failed', { maestroAgentName: agent.name })}
          </Typography>
        ) : null}
        <div className={contactStyles.viewerComposerRow}>
          <textarea
            className={contactStyles.viewerComposerField}
            value={state.text}
            disabled={sending}
            onChange={(e) => dispatch({ type: 'edit', text: e.target.value })}
            placeholder={placeholder}
            aria-label={placeholder}
            rows={1}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter is a newline. IME composition is left to complete.
              if (e.key === 'Enter' && !e.shiftKey && !isImeComposing(e)) {
                e.preventDefault();
                void submit();
              }
            }}
          />
          <IconButton
            size="small"
            variant="contained"
            color="standard"
            type="submit"
            disabled={!canSendComposer(state)}
            aria-label={t('common.send')}
          >
            <ArrowUp size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
          </IconButton>
        </div>
      </div>
      {scope.kind === 'thread' ? (
        <Typography variant="p3" color="secondary" className={styles.composerNote}>
          {t('outreach.composer.hint', { maestroAgentName: agent.name, name: scope.contactName })}
        </Typography>
      ) : contactLine ? (
        <Typography variant="p3" color="secondary" className={styles.composerNote} dir="auto">
          {renderLaneLine(contactLine, tr, agent.name)}
        </Typography>
      ) : null}
    </form>
  );
}
